import {useState, useEffect, useCallback, useRef} from 'react';
import youtube from '../apis/youtube';

// --- AUDIO-version preference ------------------------------------------------
// Guests usually want the plain "audio" upload of a track (e.g.
// "(Official Audio)", "[Audio]", "Audio Only") rather than the music-video,
// live, or reaction version. We re-rank YouTube results so titles mentioning
// "audio" float to the top. Ties keep YouTube's own relevance order, so this
// only reshuffles when an audio version actually exists in the results.
//
// `\baudio\b` uses word boundaries, so band/album names like "Audioslave" or
// "audiobook" (no boundary after "audio") are NOT falsely boosted.
const audioScore = (item) => {
  const title = (item && item.snippet && item.snippet.title) ? item.snippet.title.toLowerCase() : '';
  let score = 0;
  if (/\bofficial\s+audio\b/.test(title)) score += 120;     // best: official audio upload
  else if (/\baudio\b/.test(title)) score += 100;           // any "audio" mention
  if (/\baudio\s+only\b/.test(title)) score += 20;          // "audio only" is a strong signal
  // Gentle demotion of clearly non-audio versions so they don't outrank audio.
  if (/\blive\b/.test(title)) score -= 15;
  if (/\b(music\s+video|official\s+video)\b/.test(title)) score -= 6;
  return score;
};

// Stable re-rank: highest audio score first, original order preserved on ties.
export const rankByAudioPreference = (items) => {
  if (!Array.isArray(items)) return items;
  return items
    .map((item, index) => ({ item, index, score: audioScore(item) }))
    .sort((a, b) => (b.score - a.score) || (a.index - b.index))
    .map((entry) => entry.item);
};

const useVideos = (defaultSearchTerm) => {
  const [videos, setVideos] = useState([]);
  const searchCountRef = useRef(0);
  const lastSearchTimeRef = useRef(0);

  const getAvailableApiKeys = () => {
    const keys = [];
    if (process.env.REACT_APP_YOUTUBE_API_KEY) keys.push(process.env.REACT_APP_YOUTUBE_API_KEY);
    if (process.env.REACT_APP_YOUTUBE_API_KEY2) keys.push(process.env.REACT_APP_YOUTUBE_API_KEY2);
    if (process.env.REACT_APP_YOUTUBE_API_KEY3) keys.push(process.env.REACT_APP_YOUTUBE_API_KEY3);
    if (process.env.REACT_APP_YOUTUBE_API_KEY4) keys.push(process.env.REACT_APP_YOUTUBE_API_KEY4);
    if (process.env.REACT_APP_YOUTUBE_API_KEY5) keys.push(process.env.REACT_APP_YOUTUBE_API_KEY5);
    if (process.env.REACT_APP_YOUTUBE_API_KEY6) keys.push(process.env.REACT_APP_YOUTUBE_API_KEY6);
    return keys;
  };

  const search = useCallback(async (term) => {
    if (!term || term.trim().length === 0) {
      setVideos([]);
      return;
    }

    searchCountRef.current += 1;
    const timeSinceLastSearch = Date.now() - lastSearchTimeRef.current;
    lastSearchTimeRef.current = Date.now();

    // Block rapid fire calls (potential infinite loop)
    if (timeSinceLastSearch < 100 && searchCountRef.current > 3) {
      console.error('[YTQUOTA] Rapid fire detected, blocking search.');
      return;
    }

    const availableKeys = getAvailableApiKeys();

    if (availableKeys.length === 0) {
      console.error('[YTQUOTA] No API keys configured in .env file');
      setVideos([]);
      return;
    }

    console.log(`[YTQUOTA] ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━`);
    console.log(`[YTQUOTA] API CALL #${searchCountRef.current} | Search: "${term}"`);
    console.log(`[YTQUOTA] Cost: 100 units | Keys available: ${availableKeys.length}`);
    console.log(`[YTQUOTA] Time since last search: ${timeSinceLastSearch}ms`);
    console.log(`[YTQUOTA] ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━`);

    // Shuffle keys to distribute quota usage
    const shuffledKeys = [...availableKeys].sort(() => Math.random() - 0.5);

    for (let i = 0; i < shuffledKeys.length; i++) {
      const apiKey = shuffledKeys[i];
      const maskedKey = apiKey.substring(0, 10) + '...' + apiKey.substring(apiKey.length - 4);

      try {
        const response = await youtube.get('/search', {
          params: {
            q: term,
            part: 'snippet',
            // Fetch a wider pool (search costs 100 units regardless of size) so
            // an "audio" version ranked below the music video can still surface.
            maxResults: 10,
            type: 'video',
            key: apiKey,
          },
        });

        console.log(`[YTQUOTA] SUCCESS with key ${i + 1}/${shuffledKeys.length} (${maskedKey}) | ${response.data.items.length} results`);
        console.log(`[YTQUOTA] Total quota used this session: ${searchCountRef.current * 100} units`);
        // Prefer the AUDIO version, then trim back to the display count.
        setVideos(rankByAudioPreference(response.data.items).slice(0, 5));
        return;

      } catch (error) {
        if (error.response && error.response.status === 403) {
          console.error(`[YTQUOTA] 403 QUOTA EXCEEDED - key ${i + 1}/${shuffledKeys.length} (${maskedKey})`);
          if (i === shuffledKeys.length - 1) {
            console.error(`[YTQUOTA] ALL ${shuffledKeys.length} KEYS EXHAUSTED | Total quota used: ${searchCountRef.current * 100} units`);
            setVideos([]);
          }
        } else {
          console.error(`[YTQUOTA] ERROR with key ${i + 1}/${shuffledKeys.length}: ${error.message}`);
          if (i === shuffledKeys.length - 1) {
            setVideos([]);
          }
        }
      }
    }
  }, []);

  useEffect(() => {
    if (defaultSearchTerm && defaultSearchTerm.trim().length > 0) {
      search(defaultSearchTerm);
    }
  }, [defaultSearchTerm, search]);

  return [videos, search];
};

export default useVideos;
